import { memo } from "react";
import { HelpdeskTicketInterface } from "shared/types/helpdesk";

export interface MergeTicketsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  sourceTicket: HelpdeskTicketInterface | null;
  targetTicket: HelpdeskTicketInterface | null;
  onSourceChange: (ticket: HelpdeskTicketInterface | null) => void;
  onTargetChange: (ticket: HelpdeskTicketInterface | null) => void;
  sourceSearchValue: string;
  targetSearchValue: string;
  onSourceSearchChange: (value: string) => void;
  onTargetSearchChange: (value: string) => void;
  allTickets: HelpdeskTicketInterface[];
}

/**
 * MergeTicketsModal - Modal for merging two helpdesk tickets
 *
 * Features:
 * - Search and select source and target tickets
 * - Validation to prevent merging a ticket with itself
 * - Clear indication of which ticket will be deleted (source) and which will be kept (target)
 * - Shows confirmation summary before merging
 */
const MergeTicketsModalComponent = ({
  isOpen,
  onClose,
  onConfirm,
  sourceTicket,
  targetTicket,
  onSourceChange,
  onTargetChange,
  sourceSearchValue,
  targetSearchValue,
  onSourceSearchChange,
  onTargetSearchChange,
  allTickets,
}: MergeTicketsModalProps) => {
  if (!isOpen) return null;

  // Filter tickets based on search term
  const getSortedFilteredTickets = (searchTerm: string = "") => {
    let filtered = allTickets;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = allTickets.filter(
        (ticket) =>
          ticket.id?.toString().includes(term) ||
          ticket.title.toLowerCase().includes(term),
      );
    }

    // Sort by ID (numeric sort)
    return filtered.sort((a, b) => (a.id || 0) - (b.id || 0));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-neutral-primary mb-4 text-lg font-semibold">
          Tickets samenvoegen
        </div>
        <div className="space-y-4">
          <div className="text-neutral-primary bg-neutral-primary/5 rounded-lg p-3 text-sm">
            Kies het bron-ticket en het doel-ticket. E-mails en notities worden
            overgezet van bron naar doel. Bron wordt daarna verwijderd.
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Source Ticket */}
            <div>
              <div className="text-neutral-secondary-mid mb-2 text-xs font-medium">
                Bron (wordt verwijderd)
              </div>

              {/* Search input for source */}
              <input
                type="text"
                placeholder="Zoek op ID of titel..."
                className="border-neutral-primary/20 mb-2 w-full rounded-md border px-3 py-2 text-sm"
                value={sourceSearchValue}
                onChange={(e) => onSourceSearchChange(e.target.value)}
              />

              <select
                className="border-neutral-primary/20 w-full rounded-md border px-3 py-2 text-sm"
                size={8}
                value={sourceTicket?.id || ""}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const ticket =
                    getSortedFilteredTickets(sourceSearchValue).find(
                      (t) => t.id === id,
                    ) || null;
                  onSourceChange(ticket);
                }}
              >
                <option value="">Selecteer een ticket...</option>
                {getSortedFilteredTickets(sourceSearchValue).map((ticket) => (
                  <option key={ticket.id} value={ticket.id}>
                    H{ticket.id} •{" "}
                    {ticket.title.length > 40
                      ? `${ticket.title.substring(0, 40)}...`
                      : ticket.title}
                  </option>
                ))}
              </select>

              {/* Selected source info */}
              {sourceTicket && (
                <div className="mt-2 rounded-sm border border-red-200 bg-red-50 p-2 text-xs">
                  <div className="font-medium text-red-800">
                    H{sourceTicket.id}
                  </div>
                  <div className="text-red-600">{sourceTicket.title}</div>
                </div>
              )}
            </div>

            {/* Target Ticket */}
            <div>
              <div className="text-neutral-secondary-mid mb-2 text-xs font-medium">
                Doel (blijft bestaan)
              </div>

              {/* Search input for target */}
              <input
                type="text"
                placeholder="Zoek op ID of titel..."
                className="border-neutral-primary/20 mb-2 w-full rounded-md border px-3 py-2 text-sm"
                value={targetSearchValue}
                onChange={(e) => onTargetSearchChange(e.target.value)}
              />

              <select
                className="border-neutral-primary/20 w-full rounded-md border px-3 py-2 text-sm"
                size={8}
                value={targetTicket?.id || ""}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const ticket =
                    getSortedFilteredTickets(targetSearchValue).find(
                      (t) => t.id === id,
                    ) || null;
                  onTargetChange(ticket);
                }}
              >
                <option value="">Selecteer een ticket...</option>
                {getSortedFilteredTickets(targetSearchValue).map((ticket) => (
                  <option key={ticket.id} value={ticket.id}>
                    H{ticket.id} •{" "}
                    {ticket.title.length > 40
                      ? `${ticket.title.substring(0, 40)}...`
                      : ticket.title}
                  </option>
                ))}
              </select>

              {/* Selected target info */}
              {targetTicket && (
                <div className="bg-moss-primary/10 border-moss-primary/30 mt-2 rounded-sm border p-2 text-xs">
                  <div className="text-moss-primary font-medium">
                    H{targetTicket.id}
                  </div>
                  <div className="text-neutral-primary">{targetTicket.title}</div>
                </div>
              )}
            </div>
          </div>

          {/* Merge confirmation */}
          {sourceTicket &&
            targetTicket &&
            sourceTicket.id !== targetTicket.id && (
              <div className="text-neutral-primary rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm">
                <div className="mb-1 font-medium">Samenvoegingsoverzicht:</div>
                <div>
                  E-mails en notities worden overgezet van{" "}
                  <span className="font-semibold text-red-600">
                    H{sourceTicket.id}
                  </span>{" "}
                  naar{" "}
                  <span className="text-moss-primary font-semibold">
                    H{targetTicket.id}
                  </span>
                </div>
                <div className="text-neutral-secondary-mid mt-1 text-xs">
                  Ticket H{sourceTicket.id} wordt definitief verwijderd na de
                  samenvoeging.
                </div>
              </div>
            )}

          {/* Error if same ticket selected */}
          {sourceTicket &&
            targetTicket &&
            sourceTicket.id === targetTicket.id && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                Je kan een ticket niet samenvoegen met zichzelf. Kies twee
                verschillende tickets.
              </div>
            )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            className="border-neutral-primary/20 hover:bg-neutral-primary/5 rounded-md border px-4 py-2 text-sm transition-colors"
            onClick={onClose}
          >
            Annuleren
          </button>
          <button
            className="bg-moss-primary hover:bg-moss-primary/90 rounded-md px-4 py-2 text-sm text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            disabled={
              !sourceTicket ||
              !targetTicket ||
              sourceTicket.id === targetTicket.id
            }
            onClick={onConfirm}
          >
            Tickets samenvoegen
          </button>
        </div>
      </div>
    </div>
  );
};

export const MergeTicketsModal = memo(MergeTicketsModalComponent);
